import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, Coins, Languages } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { STEPS } from '../components/pipeline/Stepper';
import IdeaStage from '../components/pipeline/IdeaStage';
import MarketStage from '../components/pipeline/MarketStage';
import BuildPackStage from '../components/pipeline/BuildPackStage';
import PrototypeStage from '../components/pipeline/PrototypeStage';
import ForkSwitch from '../components/pipeline/ForkSwitch';
import PathBStage from '../components/pipeline/PathBStage';
import { Skeleton } from '../components/pipeline/ui';
import { formatCreditsOnly } from '../lib/credits';
import { invalidateHomeSummary } from '../lib/homeSummary';

function WalletBadge({ wallet }) {
  const { t } = useTranslation();
  let content;
  if (!wallet) content = <Skeleton className="h-4 w-16" />;
  else content = t('wallet.credits', { balance: formatCreditsOnly(wallet.balance_credits ?? 0) });
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground font-data" data-testid="wallet-badge">
      <Coins className="h-4 w-4 text-warning" />
      {content}
    </div>
  );
}

function LanguageBadge({ language }) {
  const { t } = useTranslation();
  if (!language) return null;
  return (
    <span
      className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-muted text-muted-foreground border border-border"
      title={t('project.languageBadge', { language: t(`language.${language}`) })}
      data-testid="project-language-badge"
    >
      <Languages className="h-3.5 w-3.5" />
      {t(`language.${language}`)}
    </span>
  );
}

export default function ProjectPage() {
  // The route now says WHICH artifact is being opened. Without this every dashboard card
  // landed on the first step, so a market card opened the idea page.
  const { id, stage: routeStage } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [stage, setStage] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [manifestReady, setManifestReady] = useState(false); // A1: entitlements resolved
  const [walletInfo, setWalletInfo] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.out('get_project_state', { project_id: id });
      setState(r);
    } catch (err) {
      toast.error(err?.message || t('common.loadFailed'));
    }
  }, [id, t]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    api.getManifest().then(setManifest).catch(() => setManifest(null)).finally(() => setManifestReady(true));
    api.getWallet().then(setWalletInfo).catch(() => {});
    // Generating anything in here changes what the dashboards count, so leaving drops the
    // cached summary rather than letting a stale one be served for the rest of the TTL.
    return () => { invalidateHomeSummary(); };
  }, []);

  const completed = state?.latest_versions || {};
  const hasIdea = !!completed.idea_object;
  const language = state?.project?.language;

  // Pick the initial stage (review projects open on Path B) and keep the active stage on a
  // visible step. Waits for both manifest + project state so `origin` is known before choosing.
  useEffect(() => {
    const vis = STEPS.filter(
      (s) => !manifest?.entitlements || manifest.entitlements?.[s.cap.group]?.[s.cap.key] !== false,
    );
    if (!vis.length) return;
    if (stage === null) {
      if (!state) return;
      // A stage named in the URL wins, but only if the tenant is entitled to see it -
      // the URL is user input, and entitlement is decided here and again on the server.
      if (routeStage && vis.some((s) => s.key === routeStage)) { setStage(routeStage); return; }
      const prefersPathB = state?.project?.origin === 'review' && vis.some((s) => s.key === 'pathb');
      setStage(prefersPathB ? 'pathb' : vis[0].key);
      return;
    }
    // In-app navigation (e.g. the Blueprint/Prototype fork) changes the route AFTER first
    // load; without this the URL moved but the rendered stage did not until a full reload.
    if (routeStage && routeStage !== stage && vis.some((s) => s.key === routeStage)) {
      setStage(routeStage);
      return;
    }
    if (!vis.some((s) => s.key === stage)) setStage(vis[0].key);
  }, [manifest, routeStage, stage, state]);

  return (
    <>
      {!manifestReady ? (
        <div data-testid="pipeline-loading">
          <Skeleton className="h-12 w-full mb-6" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <>

          <main data-testid="pipeline-content">
            {stage === 'idea' && (
              <IdeaStage
                projectId={id}
                language={language}
                origin={state?.project?.origin}
                selectedIndustryKey={state?.project?.selected_industry_key}
                selectedIndustryLang={state?.project?.selected_industry_lang}
                onChanged={refresh}
                servedModels={state?.served_models}
              />
            )}
            {stage === 'market' && <MarketStage projectId={id} hasIdea={hasIdea} onChanged={refresh} servedModels={state?.served_models} />}
            {/* The customize box is GONE (Ali, 2026-08-03). The user edits exactly two fields
                — ideal customer and key features — via the Edit buttons on the idea itself,
                saved directly with no model call. LLM refinement of an idea is Regenerate. */}
            {(stage === 'build' || stage === 'prototype') && (
              <ForkSwitch
                projectId={id}
                stage={stage}
                showBuild={manifest?.entitlements?.paths?.build_pack !== false}
                showPrototype={manifest?.entitlements?.paths?.app_mock !== false}
              />
            )}
            {stage === 'build' && <BuildPackStage projectId={id} hasIdea={hasIdea} onChanged={refresh} language={language} servedModels={state?.served_models} />}
            {stage === 'prototype' && <PrototypeStage projectId={id} hasIdea={hasIdea} onChanged={refresh} servedModels={state?.served_models} projectName={state?.project?.name} />}
            {stage === 'pathb' && <PathBStage projectId={id} userId={user?.id} onChanged={refresh} />}
          </main>
        </>
      )}
    </>
  );
}
